"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { track } from "@/lib/analytics"
import { ChatMessage } from "./chat-message"
import {
  MessageScrollerProvider,
  MessageScroller,
  MessageScrollerViewport,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerButton,
} from "./primitives/message-scroller"
import { StarterPrompts } from "./starter-prompts"
import Image from "next/image"
import { isBusinessHours } from "@/lib/business-hours"
import { captureChatError, captureChatMessage } from "@/lib/sentry-chat"
import { parseSseFrames, type SseEvent } from "@/lib/chat/parse-sse"
import { isBotEcho } from "@/lib/chat/bot-echo"
import {
  fetchConversationMessages,
  selectOutgoingMessages,
} from "@/lib/chat/recover-turn"
import { localizeSources } from "@/lib/chat/localize-sources"
import type { AiStatus } from "./use-ai-answer"
import { getSessionId } from "@/lib/chat/session-id"
import { LAUNCHER_TEAM } from "./launcher-team"
import type { ChatSessionHandle, ChatTurn } from "./use-chat-session"
import { usePromptVariant } from "./use-prompt-variant"
import { OutOfHoursForm } from "./out-of-hours-form"
import { EmailCollectForm } from "./email-collect-form"
import { SlowReplyForm } from "./slow-reply-form"
import { useSlowReplyPrompt } from "./use-slow-reply-prompt"
import { useAnnounceEmailAsk } from "./use-announce-email-ask"
import { useEchoLauncherLines } from "./use-echo-launcher-lines"
import { useBusinessHours } from "./use-business-hours"
import { useBusinessHoursSettings } from "./business-hours-provider"
import { useAgentsOnline } from "./use-agents-online"
import { useTabAttention } from "./use-tab-attention"
import { TypingIndicator } from "./typing-indicator"
import { getCountryConfig } from "@/i18n/config"
import {
  resolveSwitchLocale,
  switchLocaleInPath,
  languageDisplayName,
} from "@/lib/locale-switch"
import type { RelativeTimeLabels } from "./relative-time"
import {
  useChatwootSubscription,
  type ChatwootEvent,
  type IncomingMessageEvent,
} from "./use-chatwoot-subscription"
import { useReadReceipts } from "./use-read-receipts"
import { useTypingEmit } from "./use-typing-emit"

const CHATWOOT_WS_BASE =
  process.env.NEXT_PUBLIC_CHATWOOT_WS_BASE ?? "wss://chat.aheadhealth.dev"

/** How long the backend's `rate_limited` event locks the composer. */
const RATE_LIMIT_LOCKOUT_MS = 60_000

/**
 * Composer cap, in characters. Matches `MAX_QUERY_LENGTH` in glue's
 * `sanitizeQuery` — the point past which the model stops seeing the text — so
 * nothing a visitor types is silently dropped on the way to the answer. Ample
 * for the case this exists for: pasting a stack of biomarker values in one go.
 */
const COMPOSER_MAX_LENGTH = 2000

/** Show the character count once the remaining budget gets tight. */
const COMPOSER_COUNTER_THRESHOLD = COMPOSER_MAX_LENGTH - 200

/**
 * Mirrors glue's `EMAIL_IN_TEXT` (chat-route.ts): an email typed into a chat
 * message is captured onto the contact record server-side, so the widget must
 * count it as "email provided" too — otherwise the slow-reply ask fires at a
 * visitor who already handed over their address (conv 8439: the bot asked in
 * prose, the visitor typed it, the form asked again two minutes later).
 */
const EMAIL_IN_MESSAGE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/

export interface ChatPanelProps {
  scope: string
  country: string
  locale: string
  pageUrl: string
  pageTitle: string
  entryPoint: "inline" | "launcher"
  /** When set, the panel submits this prompt once on mount. */
  initialPrompt?: string
  /** Provenance of `initialPrompt` when it came from a teaser chip (the
   *  PostHog prompt_key string) — forwarded to glue as trace provenance. */
  initialPromptKey?: string
  onClose: () => void
  /** Shared session handle — owned by ContextualChat so there's a single
   *  source of truth for session state (conversation_id, turns, etc.). */
  chatSession: ChatSessionHandle
  promptScope?: string
  /** Hide the panel's own team header + close (used when a host — e.g. the
   *  biomarkers chat drawer — supplies its own header/tabs). */
  hideHeader?: boolean
  /**
   * Whether this panel owns the `chat_opened` event (AHD-9529).
   *
   * Defaults to true, which is right for a host that just mounts the panel and
   * has no opinion about why. `ContextualChat` passes false: it knows the intent
   * behind each open and fires the event itself, so leaving this on double-counts
   * every single open.
   *
   * Mounting is not the same thing as opening, which is the other half of why
   * this exists — a panel restored from `session.panel_open` after an SPA
   * navigation mounts without anyone having opened anything.
   */
  trackOpen?: boolean
}

function classifyConfidence(
  handedOff: boolean,
  textLen: number
): "high" | "med" | "low" {
  if (handedOff) return "low"
  if (textLen < 80) return "med"
  return "high"
}

function nowIso(): string {
  return new Date().toISOString()
}

/**
 * Anchors a marker turn's TOP into view once its MarkerBreakdown card has
 * mounted. The card is tall and arrives all at once (not token-by-token), so
 * the scroller's default follow-to-bottom lands the user at the very bottom of
 * the card, past the prose. We pin the reply's start instead; the card fills
 * below.
 *
 * Two subtleties the implementation is shaped around:
 *  - The card mounts via `content-visibility` and grows over several frames.
 *    The scroller's follow-bottom (a ResizeObserver) grabs each growth step and
 *    pulls to the bottom, so a single pin loses the race — we re-pin on our own
 *    ResizeObserver until the size settles.
 *  - We use the item's native `scrollIntoView({block:"start"})`, not the
 *    scroller's `scrollToMessage`. The latter marks the scroll as
 *    "autoscrolling" so the scroller stays in follow-bottom and immediately
 *    re-pulls down; a native scroll leaves follow-bottom, which is what makes
 *    the top anchor actually hold.
 *
 * Fires once per marker turn (tracked by message id), only for a finished bot
 * turn carrying a breakdown, so streaming text replies are untouched. Renders
 * nothing — it exists to run the effect inside the scroller provider.
 */
function PinMarkerTurnTop({
  turns,
  viewportRef,
}: {
  turns: ChatTurn[]
  viewportRef: React.RefObject<HTMLDivElement | null>
}) {
  const lastIndex = turns.length - 1
  const last = turns[lastIndex]
  const shouldPin =
    last?.role === "bot" &&
    !last.isStreaming &&
    (last.markerBreakdown?.markers?.length ?? 0) > 0

  // Deps are [shouldPin, lastIndex]: the effect re-runs only when a new last
  // turn appears (new lastIndex) or its pin-eligibility flips, so it pins each
  // marker turn exactly once without a manual "already pinned" ref — which would
  // otherwise wrongly skip the pin after StrictMode's mount→unmount→remount.
  useEffect(() => {
    if (!shouldPin) return
    const id = String(lastIndex)
    const viewport = viewportRef.current
    const item = viewport?.querySelector<HTMLElement>(
      `[data-slot="message-scroller-item"][data-message-id="${id}"]`
    )
    if (!viewport || !item) return

    const pin = () => item.scrollIntoView({ block: "start", behavior: "auto" })
    // The authoritative pin is the one that runs AFTER the card stops growing:
    // a native scroll then takes the scroller out of follow-bottom and holds.
    // During growth the scroller keeps pulling to the bottom, so we also re-pin
    // on each resize (a bonus that keeps the top roughly in view mid-growth) and
    // debounce a final pin once resizes go quiet. The cap stops us observing
    // forever if something animates indefinitely.
    let settle: ReturnType<typeof setTimeout>
    const scheduleSettlePin = () => {
      clearTimeout(settle)
      settle = setTimeout(pin, 120)
    }
    pin()
    scheduleSettlePin()
    const ro = new ResizeObserver(() => {
      pin()
      scheduleSettlePin()
    })
    ro.observe(item)
    const cap = setTimeout(() => ro.disconnect(), 2000)
    return () => {
      ro.disconnect()
      clearTimeout(settle)
      clearTimeout(cap)
    }
  }, [shouldPin, lastIndex, viewportRef])

  return null
}

export function ChatPanel({
  scope,
  country,
  locale,
  pageUrl,
  pageTitle,
  entryPoint,
  initialPrompt,
  initialPromptKey,
  onClose,
  chatSession,
  promptScope,
  hideHeader = false,
  trackOpen = true,
}: ChatPanelProps) {
  const t = useTranslations("chat")

  const {
    session,
    hydrated,
    setConversation,
    setEmailProvided,
    noteHandoff,
    clearHandoff,
    dismissSlowReply,
    noteEmailAskAnnounced,
    appendTurn,
    prependTurns,
    updateTurn,
    dropTurn,
    dropLastTurn,
    noteChatwootMessage,
    bufferEchoLine,
    drainEchoLines,
    setPanelOpen,
  } = chatSession

  const [handedOff, setHandedOff] = useState(false)
  // True once glue reports (on the `session` event) that it CONTINUED an
  // existing conversation for this fresh tab (AHD-10752 stitch). Drives
  // suppression of the buffered launcher-greeting echo so it doesn't land
  // mid-thread in the stitched Chatwoot conversation. State, not a ref, so the
  // echo hook re-renders with it set in the same batch the conversation id
  // arrives — the flush it gates fires on that render.
  const [stitchedSession, setStitchedSession] = useState(false)
  const [isSending, setIsSending] = useState(false)
  // Transient progress line for the in-flight bot turn (null when nothing to
  // show). Cleared on first answer token / done / error / handoff so it never
  // outlives the work it describes.
  const [aiStatus, setAiStatus] = useState<AiStatus | null>(null)
  const [inputValue, setInputValue] = useState("")
  // Backend rate-limit lockout. A boolean plus a timer, not a
  // `Date.now() < until` comparison read at render time — a bare timestamp
  // never re-renders when it lapses, so the composer stayed locked until some
  // unrelated state change happened to repaint it.
  const [rateLimited, setRateLimited] = useState(false)
  const [showOutOfHoursForm, setShowOutOfHoursForm] = useState(false)
  const [emailPromptDismissed, setEmailPromptDismissed] = useState(false)
  const [humanReplied, setHumanReplied] = useState(false)
  const businessHoursOpen = useBusinessHours()
  const businessHoursSettings = useBusinessHoursSettings()
  const agentsOnline = useAgentsOnline()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const handoffTimestamp = useRef<number | null>(null)
  const streamAbortRef = useRef<AbortController | null>(null)
  // Chatwoot ids of the bot's own answers. glue echoes every bot answer into
  // Chatwoot for the agent transcript and hands us the id via a `chatwoot_echo`
  // SSE event; we remember it here so the Chatwoot subscription drops that echo
  // rather than rendering the answer a second time (it's already on screen from
  // the SSE stream).
  const suppressedChatwootIds = useRef<Set<number>>(new Set())
  // Exact text of launcher lines the widget just asked glue to echo (the Ella
  // greeting and the system turns). Reserved synchronously — before the echo
  // POST returns the id above — so the copy Chatwoot broadcasts over the
  // subscription is dropped even when it wins the race. See isBotEcho + the echo
  // hook. This is what stops a *system*-role echoed turn (which the bot-turn
  // content match wouldn't catch) rendering as a duplicate.
  const suppressedChatwootTexts = useRef<Set<string>>(new Set())
  // Goes false the moment this panel starts unmounting. The unmount aborts the
  // in-flight stream, and the abort lands in `submit`'s catch — which used to
  // write to the session on the way out. That write persists to sessionStorage,
  // so it outlived the panel and poisoned the transcript the visitor saw when
  // they reopened (AHD-9158). Nothing writes past teardown now.
  const panelAliveRef = useRef(true)
  // Whether this mount has already asked Chatwoot what it missed.
  const caughtUp = useRef(false)
  // Whether the stitch `history` replay has already been applied this mount, so
  // a resend can't prepend the prior transcript twice (AHD-10752).
  const historyHydrated = useRef(false)
  // Whether this mount has already asked glue whether the visitor has a
  // resumable conversation (the on-open path — the new-tab equivalent of the
  // stitch `session`+`history` SSE events). Fires once per mount.
  const resumeAttempted = useRef(false)
  // The first-open welcome greeting's turn id, captured so a resume hit that
  // lands AFTER the greeting already showed can drop it — a returning visitor
  // in a fresh tab must not see "Hi, I'm Ella…" stranded under their restored
  // thread.
  const welcomeTurnId = useRef<string | null>(null)
  // Latest transcript mirrored into a ref, so the (stable) subscription handler
  // can content-match incoming messages against bot turns without being
  // recreated on every turn — which would tear down and reconnect the socket.
  const turnsRef = useRef(session.turns)
  useEffect(() => {
    turnsRef.current = session.turns
  }, [session.turns])

  // ── Echo client-generated launcher lines into Chatwoot ──
  // The widget renders a handful of canned turns itself (the Ella greeting, the
  // handoff / locale-switch / out-of-hours / slow-reply notices, the answer-
  // failed line). `echoLauncherLine` mirrors the exact localized string the
  // visitor saw into the Chatwoot conversation as a visible bot message, then
  // feeds the returned message id into the SAME suppression the bot-answer
  // `chatwoot_echo` uses — so the copy Chatwoot delivers back over the
  // subscription / catch-up poll is dropped and the visitor never sees a
  // duplicate. Lines fired before a conversation exists (the greeting) are
  // buffered and flushed once the conversation id is known.
  const onLineEchoed = useCallback(
    (chatwootMessageId: number) => {
      // Mirror the chatwoot_echo SSE handler exactly: the id set drops the live
      // copy, and advancing the high-water mark keeps the reopen catch-up poll
      // from re-fetching it after this (ref-only) set is gone on remount.
      suppressedChatwootIds.current.add(chatwootMessageId)
      noteChatwootMessage(chatwootMessageId)
    },
    [noteChatwootMessage]
  )
  const onLineEchoQueued = useCallback((text: string) => {
    suppressedChatwootTexts.current.add(text.trim())
  }, [])
  const echoLauncherLine = useEchoLauncherLines({
    authToken: session.source_id,
    conversationId: session.conversation_id,
    locale,
    pendingEchoLines: session.pending_echo_lines,
    bufferEchoLine,
    drainEchoLines,
    suppressBufferedFlush: stitchedSession,
    onEchoed: onLineEchoed,
    onEchoQueued: onLineEchoQueued,
  })

  // ── Starter prompts (A/B routed through PostHog via usePromptVariant) ──
  const {
    prompts: starterPrompts,
    variant: promptVariant,
    variantId: promptVariantId,
  } = usePromptVariant(scope, promptScope)

  // ── Analytics: opened ──
  // Only when this panel owns the event — see `trackOpen`. `setPanelOpen` runs
  // either way: it's session state, not analytics, and it's idempotent.
  const openedTracked = useRef(false)
  useEffect(() => {
    if (openedTracked.current) return
    openedTracked.current = true
    setPanelOpen(true)
    if (!trackOpen) return
    track.chat.opened({
      scope,
      country,
      locale,
      page_slug: pageUrl,
      entry_point: entryPoint,
      conversation_id: session.conversation_id ?? undefined,
    })
  }, [
    scope,
    country,
    locale,
    pageUrl,
    entryPoint,
    session.conversation_id,
    setPanelOpen,
    trackOpen,
  ])

  // Auto-scroll is handled by MessageScroller (autoScroll + scrollAnchor on the
  // last item), so there's no manual scrollIntoView effect anymore.

  // ── First-open welcome flow ──
  // On a fresh session (no turns yet) show a brief typing indicator,
  // then append the AI welcome turn. If the user arrived with an
  // `initialPrompt` queued from the teaser we skip the welcome — their
  // first question is already being answered.
  const [showWelcomeIndicator, setShowWelcomeIndicator] = useState(false)
  const welcomeAppended = useRef(false)
  // Holds the welcome flow's pending `setTimeout` id so `submit()` can cancel
  // it from outside this effect — see the cancellation block at the top of
  // `submit` for why that matters (AHD chat: two "Thinking…" bubbles).
  const welcomeTimeoutRef = useRef<number | null>(null)
  useEffect(() => {
    // Wait for the session hook to rehydrate from sessionStorage —
    // otherwise on remount (panel closed → reopened) the first render
    // sees `turns.length === 0` and we'd re-fire the welcome on top
    // of the existing transcript.
    if (!hydrated) return
    if (welcomeAppended.current) return
    if (session.turns.length > 0) {
      welcomeAppended.current = true
      return
    }
    if (initialPrompt) {
      welcomeAppended.current = true
      return
    }
    welcomeAppended.current = true
    setShowWelcomeIndicator(true)
    const id = window.setTimeout(() => {
      welcomeTimeoutRef.current = null
      setShowWelcomeIndicator(false)
      // Belt-and-braces: `submit` is what normally cancels this flow the
      // instant the visitor sends a message, but if a user turn landed here
      // anyway (a race), bail rather than append the welcome message after
      // it. `turnsRef` (not `session.turns`) because this closure was built
      // when the effect fired and doesn't see later renders.
      if (turnsRef.current.some((turn) => turn.role === "user")) return
      const welcome = t("ui.welcome_message")
      welcomeTurnId.current = appendTurn({
        role: "bot",
        content: welcome,
        ts: nowIso(),
      })
      // No conversation exists yet (the greeting fires before the first
      // message), so this buffers and flushes once the id is known.
      echoLauncherLine("welcome", welcome)
    }, 700)
    welcomeTimeoutRef.current = id
    return () => window.clearTimeout(id)
  }, [
    hydrated,
    session.turns.length,
    initialPrompt,
    appendTurn,
    echoLauncherLine,
    t,
  ])

  // ── Resume a prior conversation when the panel opens in a new tab ──
  // History lives in per-tab sessionStorage, so a visitor who reopens the chat
  // in a fresh tab sees a blank panel even though glue still holds their
  // conversation. On open we ask `POST /api/chat/resume`; on a hit we hydrate
  // the same way the AHD-10752 stitch `session`+`history` SSE events do — adopt
  // the conversation ids, mark it a continuation (so the buffered greeting echo
  // is suppressed), and prepend the prior turns. On a miss (or any error) we do
  // nothing and the panel behaves as a fresh conversation.
  useEffect(() => {
    // Wait for the session hook to rehydrate before deciding the tab is fresh —
    // the pre-hydration render always reads `conversation_id === null`.
    if (!hydrated) return
    if (resumeAttempted.current) return
    resumeAttempted.current = true
    // A restored (sessionStorage) or same-tab conversation — nothing to resume.
    if (session.conversation_id) return

    void (async () => {
      // Fire-and-forget and deliberately decoupled from the welcome flow: the
      // greeting runs on its own 700ms timer so a slow or hung `/resume` never
      // blocks it. On a hit we adopt the conversation and, if the greeting
      // already landed, take it back.
      try {
        const res = await fetch("/api/chat/resume", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: getSessionId(),
            country,
            locale,
          }),
        })
        if (!res.ok) {
          // Benign for the visitor (they get a fresh conversation), but a
          // chronic non-OK that silently disables resume must be visible —
          // same reporting `submit()` gives an `/api/chat` non-OK.
          captureChatMessage(
            "Chat /api/chat/resume returned non-OK",
            "warning",
            {
              scope,
              country,
              locale,
              extra: { status: res.status },
            }
          )
          return
        }
        const data = (await res.json()) as {
          conversation_id?: number
          auth_token?: string
          pubsub_token?: string
          turns?: { role: "user" | "bot" | "agent"; content: string }[]
        }
        // Miss (`{ resumed: false }`) — no conversation to restore.
        if (!data.conversation_id) return
        // A concurrent send won the race: `submit()` has already appended a
        // user turn and is driving glue's own AHD-10752 stitch (which sets
        // `historyHydrated` and replays history on its `history` SSE event).
        // Adopting the resumed ids now would clobber the live conversation and
        // double-prepend the transcript — so bail and let the stitch own it.
        //
        // Key on a USER turn, not raw `turns.length`: a `/resume` slower than
        // the 700ms welcome timer finds the lone first-open greeting (a single
        // bot turn) already appended. Bailing on that stranded the greeting and
        // never restored the thread — the take-back block just below is exactly
        // what's meant to drop that greeting before hydrating. Only `submit()`
        // produces a user turn, so it's the true "a real send won" signal (the
        // welcome flow above discriminates the same way).
        if (
          turnsRef.current.some((turn) => turn.role === "user") ||
          historyHydrated.current
        )
          return

        // Take back the first-open welcome: cancel its pending timer, and if it
        // already appended (a `/resume` slower than the 700ms greeting) drop the
        // greeting turn so it doesn't strand under the restored thread.
        if (welcomeTimeoutRef.current !== null) {
          window.clearTimeout(welcomeTimeoutRef.current)
          welcomeTimeoutRef.current = null
        }
        welcomeAppended.current = true
        setShowWelcomeIndicator(false)
        if (welcomeTurnId.current !== null) {
          dropTurn(welcomeTurnId.current)
          welcomeTurnId.current = null
        }

        // A continuation, not a new conversation — suppress the buffered
        // greeting echo exactly as the stitch does. Set before
        // `setConversation` so the echo flush that fires when the id lands
        // already sees it.
        setStitchedSession(true)
        // Guard the live `history` SSE path so a later stitch replay can't
        // prepend the same transcript a second time.
        historyHydrated.current = true
        setConversation({
          conversation_id: data.conversation_id,
          source_id: data.auth_token ?? "",
          pubsub_token: data.pubsub_token ?? "",
        })
        if (data.turns?.length) {
          prependTurns(
            data.turns.map((h) => ({
              role:
                h.role === "agent"
                  ? ("human" as const)
                  : h.role === "bot"
                    ? ("bot" as const)
                    : ("user" as const),
              content: h.content,
              ts: nowIso(),
            }))
          )
        }
      } catch (err) {
        captureChatError(err, {
          scope,
          country,
          locale,
          extra: { route: "resume" },
        })
      }
    })()
  }, [
    hydrated,
    session.conversation_id,
    scope,
    country,
    locale,
    setConversation,
    prependTurns,
    dropTurn,
  ])

  // ── Human typing indicator ──
  // Driven by Chatwoot's real `conversation.typing_on/off` frames — and ONLY
  // those. There used to be a post-handoff "someone is surely about to type"
  // heuristic here from before the frames were wired (AHD-9457); it showed
  // dots for minutes at a visitor nobody was replying to, which reads as a
  // promise. Real frames or no dots.
  const [agentTyping, setAgentTyping] = useState(false)
  // Bumped on every typing_on so the auto-clear timer below restarts even when
  // agentTyping is already true.
  const [agentTypingPulse, setAgentTypingPulse] = useState(0)
  // typing_off frames get dropped on the wire, so never leave the indicator
  // stuck on: clear it ~5s after the last typing_on if no typing_off arrives.
  // no-raw-ids: `id` is a timer handle passed to clearTimeout, never rendered
  useEffect(() => {
    if (!agentTyping) return
    const id = window.setTimeout(() => setAgentTyping(false), 5_000)
    return () => window.clearTimeout(id)
  }, [agentTyping, agentTypingPulse])

  const humanTyping = agentTyping

  // ── Chatwoot subscription for human messages ──
  // While a `/chat` stream is in flight, incoming `message` events are QUEUED
  // and processed only after the stream settles. Processing them live loses
  // both dedup signals for the bot's own echo: Chatwoot broadcasts
  // `message.created` while glue's sendMessage is still in flight, i.e. always
  // BEFORE the `chatwoot_echo` SSE event can deliver the id — and the
  // content-match backstop compares against a bot turn that is only partially
  // rendered. Worse, a real agent reply appended mid-stream used to become
  // "the last turn" and swallow the remaining deltas (conv 132 — the answer
  // rendered twice while the real bot turn spun "thinking" forever). Deferring
  // until the stream ends makes both dedup signals reliable; a genuine agent
  // reply during a bot turn shows a couple of seconds later, which is fine.
  const streamInFlightRef = useRef(false)
  const pendingWsMessages = useRef<IncomingMessageEvent[]>([])
  // Chatwoot message ids this mount has already accounted for — via the
  // subscription OR the reopen catch-up below. Delivery is at-least-once: the
  // same message can arrive over BOTH the WebSocket and the polling bridge
  // that covers socket gaps (each path dedups only against itself), and the
  // bridge's first tick fetches with no `after` bound (its cursor is a
  // per-mount ref starting null), so it can also redeliver a message the
  // catch-up already rendered this mount. A second delivery of an agent reply
  // rendered a duplicate human turn. Seeding the bridge's cursor from the
  // persisted high-water mark is a separate, pre-existing gap tracked as a
  // follow-up; this set is the panel-side guard.
  const processedWsMessageIds = useRef<Set<number>>(new Set())

  const processIncomingMessage = useCallback(
    (e: IncomingMessageEvent) => {
      // Drop a redelivery outright — everything below (echo suppression, the
      // handoff bookkeeping, the append itself) already ran for this id.
      if (processedWsMessageIds.current.has(e.messageId)) return
      processedWsMessageIds.current.add(e.messageId)

      // Accounted for from here on, whichever way it goes below — rendered,
      // dropped as an echo, or ignored. The high-water mark is what a reopened
      // panel uses to work out what it missed, so it has to move for every
      // message we've seen, not just the ones we render.
      noteChatwootMessage(e.messageId)

      // Drop the bot's own answer echoing back over the Chatwoot subscription
      // (already rendered from the SSE stream). See isBotEcho for the why.
      if (
        isBotEcho(
          { messageId: e.messageId, content: e.content },
          {
            suppressedIds: suppressedChatwootIds.current,
            suppressedTexts: suppressedChatwootTexts.current,
            turns: turnsRef.current,
          }
        )
      )
        return

      // Only real agent/human replies remain.
      if (e.senderType !== "agent" && e.senderType !== "user") return

      appendTurn({
        role: "human",
        content: e.content,
        ts: e.timestamp,
        agentName: e.senderName,
        agentAvatarUrl: e.senderAvatarUrl,
        attachments: e.attachments,
      })

      // A human replied, so nothing is waiting any more. The analytics ping is
      // one-shot per mount (that's what the ref is for), but clearing the
      // PERSISTED marker must not be gated on it: after a collapse and reopen
      // the ref is gone while the marker isn't, and a stale marker would fire
      // the slow-reply email ask at a visitor who has already been answered.
      if (handoffTimestamp.current) {
        track.chat.humanReplyReceived({
          scope,
          conversation_id: session.conversation_id ?? 0,
          wait_ms: Date.now() - handoffTimestamp.current,
        })
        handoffTimestamp.current = null
      }
      setHumanReplied(true)
      clearHandoff()
    },
    [
      appendTurn,
      noteChatwootMessage,
      clearHandoff,
      scope,
      session.conversation_id,
    ]
  )

  const flushPendingWsMessages = useCallback(() => {
    const queued = pendingWsMessages.current
    pendingWsMessages.current = []
    // Post-teardown the session must not be written (AHD-9158); the high-water
    // mark wasn't advanced for queued messages, so reopen catch-up finds them.
    if (!panelAliveRef.current) return
    queued.forEach(processIncomingMessage)
  }, [processIncomingMessage])

  const onChatwootEvent = useCallback(
    (e: ChatwootEvent) => {
      if (e.type === "typing") {
        setAgentTyping(e.typing)
        // Restart the auto-clear timer on each typing_on frame.
        if (e.typing) setAgentTypingPulse((n) => n + 1)
        return
      }
      if (e.type !== "message") return
      if (streamInFlightRef.current) {
        pendingWsMessages.current.push(e)
        return
      }
      processIncomingMessage(e)
    },
    [processIncomingMessage]
  )

  useChatwootSubscription({
    chatwootBaseUrl: CHATWOOT_WS_BASE,
    pubsubToken: session.pubsub_token,
    sourceId: session.source_id,
    conversationId: session.conversation_id,
    onEvent: onChatwootEvent,
  })

  // ── Read receipts ──
  // Tell Chatwoot the visitor has read the latest messages (advances the
  // conversation's contact_last_seen_at → the dashboard "Seen" indicator).
  // The hook only runs while this panel is mounted (i.e. open); it additionally
  // gates on the tab being focused and the bottom sentinel below being on
  // screen, so we never mark read while backgrounded or scrolled up in history.
  // `source_id` is the per-contact widget JWT (sent as X-Auth-Token);
  // `last_chatwoot_message_id` bumps on each inbound message, re-triggering it.
  const { anchorRef: seenAnchorRef } = useReadReceipts({
    conversationId: session.conversation_id,
    authToken: session.source_id,
    latestMessageId: session.last_chatwoot_message_id,
  })

  // Scroll viewport, handed to PinMarkerTurnTop so it can top-anchor a marker
  // reply within this widget's own scroller (not a global query).
  const scrollViewportRef = useRef<HTMLDivElement>(null)

  // ── Emit the customer's typing status ──
  // Tells Chatwoot the visitor is typing (or stopped) so agents see a live
  // "customer is typing" indicator. Wired into the composer onChange + form
  // submit below. Same X-Auth-Token / active-conversation gating as presence.
  const typingEmit = useTypingEmit({
    conversationId: session.conversation_id,
    authToken: session.source_id,
  })

  // ── Recovering what the stream didn't deliver ──
  // glue writes each bot answer into Chatwoot before it finishes with the SSE
  // stream, so a stream the browser lost mid-answer costs the widget the answer
  // but not the transcript. Ask Chatwoot for it rather than telling the visitor
  // something went wrong. Returns the answer text, or null when there's nothing
  // there to recover.
  const recoverAnswerFromChatwoot = useCallback(async (): Promise<
    string | null
  > => {
    const conversationId = session.conversation_id
    const sourceId = session.source_id
    if (!conversationId || !sourceId) return null

    const afterId = session.last_chatwoot_message_id
    let messages
    try {
      messages = await fetchConversationMessages({
        conversationId,
        sourceId,
        afterId,
      })
    } catch (err) {
      // The heal itself failed. Report it — the visitor is about to be told the
      // turn failed, and if that was wrong we want to know why.
      captureChatError(err, {
        scope,
        country,
        locale,
        conversation_id: conversationId,
        prompt_variant: promptVariant,
        handed_off: handedOff,
        entry_point: entryPoint,
      })
      return null
    }

    const unrendered = selectOutgoingMessages(messages, { afterId }).filter(
      (m) =>
        !isBotEcho(
          { messageId: m.id, content: m.content },
          {
            suppressedIds: suppressedChatwootIds.current,
            suppressedTexts: suppressedChatwootTexts.current,
            turns: turnsRef.current,
          }
        )
    )

    // The answer to the message just sent is the newest one. Anything older is
    // the subscription's business, not this recovery's.
    const answer = unrendered[unrendered.length - 1]
    if (!answer) return null

    suppressedChatwootIds.current.add(answer.id)
    noteChatwootMessage(answer.id)
    return answer.content
  }, [
    session.conversation_id,
    session.source_id,
    session.last_chatwoot_message_id,
    noteChatwootMessage,
    scope,
    country,
    locale,
    promptVariant,
    handedOff,
    entryPoint,
  ])

  // Every stream-failure path ends here: ask Chatwoot first, and only say
  // something to the visitor when the turn genuinely produced nothing.
  const finishFailedTurn = useCallback(
    async (botTurnId: string, streamedText: string) => {
      if (!panelAliveRef.current) return
      const recovered = await recoverAnswerFromChatwoot()
      if (!panelAliveRef.current) return

      if (recovered) {
        // Chatwoot's copy is the complete one — it beats a partial stream.
        updateTurn(botTurnId, { content: recovered, isStreaming: false })
        return
      }
      // Whatever did arrive is better than an apology. Keep it and say nothing.
      if (streamedText) return

      // Nothing streamed and Chatwoot has nothing: a real failure. Take back the
      // empty bubble and say so as the interface, not as Ella.
      dropTurn(botTurnId, (turn) => turn.content === "")
      const failed = t("ui.answer_failed")
      appendTurn({
        role: "system",
        content: failed,
        ts: nowIso(),
      })
      echoLauncherLine("answer_failed", failed)
    },
    [
      recoverAnswerFromChatwoot,
      updateTurn,
      dropTurn,
      appendTurn,
      echoLauncherLine,
      t,
    ]
  )

  // ── Submit message ──
  const submit = useCallback(
    // `promptKey` marks a message that came from a prompt chip (starter or
    // teaser) rather than typing — forwarded to glue as trace provenance.
    async (text: string, promptKey?: string) => {
      if (!text.trim() || isSending) return

      // Cancel any in-flight first-open welcome flow. Without this, sending
      // within the 700ms welcome window left the welcome "Thinking…" bubble
      // on screen alongside this turn's own streaming bubble (two at once),
      // and the timer later appended a stray welcome message after the
      // visitor's question.
      if (welcomeTimeoutRef.current !== null) {
        window.clearTimeout(welcomeTimeoutRef.current)
        welcomeTimeoutRef.current = null
      }
      setShowWelcomeIndicator(false)
      welcomeAppended.current = true

      setIsSending(true)
      setAiStatus(null)
      const trimmed = text.trim()
      if (EMAIL_IN_MESSAGE.test(trimmed)) setEmailProvided()
      const turnIndex = session.turns.length

      // Append user turn
      appendTurn({
        role: "user",
        content: trimmed,
        ts: nowIso(),
      })

      // Append the streaming bot turn and hold on to ITS id: the Chatwoot
      // subscription can append turns while this stream is running, so every
      // patch below targets this turn by identity, never "the last turn".
      const botTurnId = appendTurn({
        role: "bot",
        content: "",
        ts: nowIso(),
        isStreaming: true,
      })

      // Queue Chatwoot messages until this turn settles — see the subscription
      // block above. Cleared (and the queue flushed) in the finally.
      streamInFlightRef.current = true

      const streamStart = Date.now()
      let accumulated = ""
      let streamFailed = false
      let currentConversationId = session.conversation_id ?? 0

      try {
        // Abort any previous in-flight stream before starting a new one
        streamAbortRef.current?.abort()
        const abortController = new AbortController()
        streamAbortRef.current = abortController

        const res = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: abortController.signal,
          body: JSON.stringify({
            session_id: getSessionId(),
            scope,
            country,
            locale,
            page_url: pageUrl,
            page_title: pageTitle,
            entry_point: entryPoint,
            message: trimmed,
            prompt_key: promptKey,
            // 0-based index of the turn being sent (0 = the turn that produces
            // the first bot answer). The same value the followup analytics
            // stamp; glue keys turn-aware behaviour off it. Additive — old glue
            // ignores it.
            turn_index: turnIndex,
            conversation_id: session.conversation_id ?? undefined,
            auth_token: session.source_id ?? undefined,
            // Whether support is reachable right now. Glue feeds this to the
            // model so it can set honest expectations when it escalates (and
            // ask for an email when nobody is coming), and uses it for handoff
            // routing in place of Chatwoot agent presence — an agent who leaves
            // the dashboard open overnight otherwise reads as "online" at 03:00.
            // Omitted while the first check is still pending; glue then falls
            // back to presence.
            support_open: businessHoursOpen ?? undefined,
          }),
        })

        // Glue assigns the conversation id and returns it in the response
        // header before the SSE body starts; the `session` frame that also
        // carries it arrives later, inside the body (see the switch below). Read
        // the header now, so the opening turn's `chat_message_sent` reports the
        // real id — otherwise it defaults to 0 and every conversation's first
        // user turn lands in a bogus `conversation_id=0` bucket that pools
        // unrelated visitors and makes turns-per-conversation uncomputable.
        const headerConversationId = Number(
          res.headers.get("X-Conversation-Id")
        )
        if (
          Number.isInteger(headerConversationId) &&
          headerConversationId > 0
        ) {
          currentConversationId = headerConversationId
        }

        track.chat.messageSent({
          scope,
          turn_index: turnIndex,
          // Report the real id, or nothing on the rare no-conversation path
          // (e.g. a rate-limited request creates none) — never the 0 sentinel.
          conversation_id:
            currentConversationId > 0 ? currentConversationId : undefined,
          message_length: trimmed.length,
        })

        if (!res.ok || !res.body) {
          captureChatMessage("Chat /api/chat returned non-OK", "error", {
            scope,
            country,
            locale,
            conversation_id: session.conversation_id,
            prompt_variant: promptVariant,
            handed_off: handedOff,
            extra: { status: res.status },
          })
          updateTurn(botTurnId, { isStreaming: false })
          await finishFailedTurn(botTurnId, "")
          setIsSending(false)
          return
        }

        // Read SSE stream
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ""

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })

          const { events, rest, done: sawDone } = parseSseFrames(buffer)
          buffer = rest
          for (const msg of events as SseEvent[]) {
            switch (msg.event) {
              case "session":
                if (msg.conversation_id) {
                  currentConversationId = msg.conversation_id
                  // AHD-10752: a continuation, not a new conversation — suppress
                  // the buffered greeting echo (set in the SAME batch as the
                  // conversation id, so the echo flush that fires on this event
                  // sees it). Never unset: a stitched session stays a
                  // continuation for its lifetime.
                  if (msg.stitched) setStitchedSession(true)
                  setConversation({
                    conversation_id: msg.conversation_id,
                    source_id: msg.auth_token ?? "",
                    pubsub_token: msg.pubsub_token ?? "",
                    email_collect: msg.email_collect,
                    slow_reply_prompt_ms: msg.slow_reply_prompt_ms,
                  })
                }
                break

              case "history":
                // AHD-10752: glue stitched this fresh tab onto an existing
                // conversation and is replaying its prior turns. Submit already
                // appended this turn's user + streaming-bot turns, so the prior
                // history goes ahead of them. Once per mount.
                if (msg.turns?.length && !historyHydrated.current) {
                  historyHydrated.current = true
                  prependTurns(
                    msg.turns.map((h) => ({
                      role:
                        h.role === "agent"
                          ? ("human" as const)
                          : h.role === "bot"
                            ? ("bot" as const)
                            : ("user" as const),
                      content: h.content,
                      ts: nowIso(),
                    }))
                  )
                }
                break

              case "message_meta":
                // Backend message id for this bot turn (== Langfuse trace id).
                // Retained on the turn for trace correlation / observability.
                if (msg.message_id) {
                  updateTurn(botTurnId, { messageId: msg.message_id })
                }
                break

              case "chatwoot_echo":
                // glue posted this bot answer into Chatwoot and handed us its
                // message id — remember it so the Chatwoot subscription drops
                // the echo instead of rendering the answer twice.
                if (typeof msg.chatwoot_message_id === "number") {
                  suppressedChatwootIds.current.add(msg.chatwoot_message_id)
                  noteChatwootMessage(msg.chatwoot_message_id)
                }
                break

              case "progress":
                if (msg.phase) {
                  setAiStatus({
                    phase: msg.phase,
                    index: msg.index ?? null,
                    total: msg.total ?? null,
                  })
                }
                break

              case "delta": {
                const chunk = msg.text ?? ""
                if (chunk.includes("HANDOFF_TO_HUMAN")) break
                // First answer token — the status line has served its purpose.
                if (!accumulated) setAiStatus(null)
                accumulated += chunk
                updateTurn(botTurnId, { content: accumulated })
                break
              }

              case "attachments":
                // Files/images on this turn (AHD-7851). Dormant until the
                // backend emits the event; shape matches ChatAttachment.
                if (msg.attachments?.length) {
                  updateTurn(botTurnId, { attachments: msg.attachments })
                }
                break

              case "marker_breakdown":
                // Deterministic biomarker-list breakdown from glue's resolver
                // (projected facts). Attaches to the streaming bot turn; the
                // MarkerBreakdown card renders it beneath the prose.
                if (msg.markerBreakdown?.markers?.length) {
                  updateTurn(botTurnId, {
                    markerBreakdown: msg.markerBreakdown,
                  })
                }
                break

              case "pricing_card": {
                // Deterministic package/price card (AHD-11297). glue emits the
                // authoritative package prices (and, for a focused turn, the
                // selected add-on slugs); attaches to the streaming bot turn and
                // renders beside the prose. Additive — the LLM still writes the
                // reply. An add-on-only card carries no packages, so accept
                // either rows present.
                const pc = msg.pricingCard
                if (pc && (pc.packages?.length || pc.addons?.length)) {
                  updateTurn(botTurnId, { pricingCard: pc })
                }
                break
              }

              case "sources":
                // KB pages the bot cited for this turn. Sanitized server-side;
                // harden against the active locale (drop cross-locale, re-derive
                // generic labels) before attaching to the streaming bot turn.
                if (msg.sources?.length) {
                  updateTurn(botTurnId, {
                    sources: localizeSources(msg.sources, locale),
                  })
                }
                break

              case "medical_topic":
                // The bot flagged this turn as medical territory. Attach the
                // "not medical advice" notice to the streaming bot turn — once
                // per conversation, derived from the transcript so it survives
                // reloads with the session (AHD-8753).
                if (!turnsRef.current.some((turn) => turn.medicalNotice)) {
                  updateTurn(botTurnId, { medicalNotice: true })
                }
                break

              case "suggested_questions":
                // Follow-up chips to nudge the visitor to continue. Attached to
                // this bot turn; rendered beneath the latest answer only. glue
                // gates medical turns to a static safe set (msg.source).
                if (msg.questions?.length) {
                  updateTurn(botTurnId, {
                    followups: msg.questions,
                    followupSource: msg.source,
                  })
                  track.chat.followupSuggested({
                    scope,
                    conversation_id: currentConversationId || undefined,
                    turn_index: turnIndex,
                    source: msg.source ?? "generated",
                    static_reason: msg.static_reason,
                    count: msg.questions.length,
                  })
                }
                break

              case "rate_limited":
                captureChatMessage("Chat rate-limited by backend", "info", {
                  scope,
                  country,
                  locale,
                  conversation_id: currentConversationId,
                  prompt_variant: promptVariant,
                })
                updateTurn(botTurnId, {
                  content: msg.text ?? "",
                  isStreaming: false,
                })
                setRateLimited(true)
                break

              case "crisis":
                // Record at info level so we can graph crisis-flow volume
                // by scope / locale without it acting as an alertable error.
                captureChatMessage("Chat crisis flow triggered", "info", {
                  scope,
                  country,
                  locale,
                  conversation_id: currentConversationId,
                  prompt_variant: promptVariant,
                  handed_off: true,
                })
                updateTurn(botTurnId, {
                  content: msg.text ?? "",
                  isStreaming: false,
                  isCrisis: true,
                })
                setHandedOff(true)
                handoffTimestamp.current = Date.now()
                // Persisted, not panel state — see ChatSessionState.handoff_at.
                noteHandoff(handoffTimestamp.current)
                break

              case "injection_blocked":
              case "moderation_block":
                captureChatMessage(
                  `Chat moderation blocked (${msg.event})`,
                  "warning",
                  {
                    scope,
                    country,
                    locale,
                    conversation_id: currentConversationId,
                    prompt_variant: promptVariant,
                    handed_off: true,
                    // Diagnostics so the Sentry issue is actionable: which
                    // safety rule fired + a deep link to the handed-off
                    // Chatwoot conversation (whose private note holds the full
                    // blocked content). The backend sends only the matched
                    // pattern, never raw user/bot text — no PHI in Sentry.
                    extra: {
                      reason: msg.reason,
                      matched: msg.matched,
                      chatwoot_url: msg.chatwoot_url,
                    },
                  }
                )
                updateTurn(botTurnId, {
                  content: msg.text ?? "",
                  isStreaming: false,
                })
                setHandedOff(true)
                handoffTimestamp.current = Date.now()
                // Persisted, not panel state — see ChatSessionState.handoff_at.
                noteHandoff(handoffTimestamp.current)
                break

              case "moderation_redacted":
                accumulated = msg.text ?? ""
                updateTurn(botTurnId, { content: accumulated })
                break

              case "handoff": {
                setHandedOff(true)
                handoffTimestamp.current = Date.now()
                // Persisted, not panel state — see ChatSessionState.handoff_at.
                noteHandoff(handoffTimestamp.current)
                // glue's verdict wins when present: it is the same value the
                // model was given, so the widget and the bot cannot disagree
                // about whether anyone is coming. The local hook is the
                // fallback and still honours the `ahead-chat-force-hours` QA
                // override.
                const inHours =
                  msg.online ??
                  businessHoursOpen ??
                  isBusinessHours(businessHoursSettings)
                if (inHours) {
                  const connecting = t("ui.connecting_team")
                  appendTurn({
                    role: "system",
                    // The reply window is a constant, so it lives inside each
                    // locale's own sentence rather than arriving as a
                    // `{timeframe}` argument — an argument is only ever as
                    // translated as its caller, and this one was hardcoded
                    // English, so DE/FR/NL read "Antwortzeit: a few minutes".
                    content: connecting,
                    ts: nowIso(),
                  })
                  echoLauncherLine("connecting_team", connecting)
                } else {
                  setShowOutOfHoursForm(true)
                }
                track.chat.handoffRequested({
                  scope,
                  conversation_id: currentConversationId,
                  reason:
                    (msg.reason as
                      | "user_requested"
                      | "low_confidence"
                      | "out_of_scope"
                      | "clinical_judgment") ?? "low_confidence",
                  turn_index: turnIndex,
                })
                break
              }

              case "handoff_active":
                setHandedOff(true)
                // Recovery path: glue emits this when the visitor messages a
                // conversation that was handed off earlier, so the real instant
                // is in the past. Keep whatever survived in the session and
                // fall back to now only when nothing did — resetting the clock
                // here would push the ask a full threshold away on every
                // message the visitor sends while waiting.
                noteHandoff(Date.now(), true)
                break

              case "suggest_locale_switch": {
                // Model-authored, and it ends up in a URL — validate against
                // the languages this country actually serves before offering.
                // CH serves de/fr/en, NL serves nl/en, so "fr" is a real offer
                // on a Swiss page and a dead link on a Dutch one.
                const target = resolveSwitchLocale(
                  msg.locale,
                  locale,
                  getCountryConfig(country)?.languages ?? []
                )
                if (!target) break
                // Name the language, not the code — this string used to
                // interpolate "en" and render "…this page in en".
                const switchOffer = t("ui.switch_locale", {
                  locale: languageDisplayName(target, locale),
                })
                appendTurn({
                  role: "system",
                  content: switchOffer,
                  ts: nowIso(),
                  localeSwitch: target,
                })
                echoLauncherLine("switch_locale", switchOffer)
                break
              }

              case "error":
                captureChatMessage(
                  "Chat backend reported error event",
                  "warning",
                  {
                    scope,
                    country,
                    locale,
                    conversation_id: currentConversationId,
                    prompt_variant: promptVariant,
                    handed_off: handedOff,
                    extra: { backend_text: msg.text },
                  }
                )
                // Handled once the stream has drained (glue sends `[DONE]` right
                // after this), so recovery doesn't stall the reader.
                streamFailed = true
                updateTurn(botTurnId, { isStreaming: false })
                break
            }
          }

          if (sawDone) {
            updateTurn(botTurnId, { isStreaming: false })
            break
          }
        }

        // Stream ended — mark done (in case [DONE] was missed)
        updateTurn(botTurnId, { isStreaming: false })

        track.chat.botResponseReceived({
          scope,
          turn_index: turnIndex + 1,
          conversation_id: currentConversationId,
          retrieval_confidence_bucket: classifyConfidence(
            handedOff,
            accumulated.length
          ),
          latency_ms: Date.now() - streamStart,
        })

        if (streamFailed) {
          await finishFailedTurn(botTurnId, accumulated)
        }
      } catch (err) {
        // When the fetch is aborted (component unmount, navigation, or a
        // new message superseding the previous stream) the reader throws
        // an AbortError or "Connection closed".  These are expected — the
        // WebSocket code already applies the same logic for benign close
        // codes.  Only report genuinely unexpected failures to Sentry.
        const isAbort = err instanceof DOMException && err.name === "AbortError"
        const isConnectionClosed =
          err instanceof Error && /connection closed/i.test(err.message)
        if (!isAbort && !isConnectionClosed) {
          captureChatError(err, {
            scope,
            country,
            locale,
            conversation_id: currentConversationId,
            prompt_variant: promptVariant,
            handed_off: handedOff,
            entry_point: entryPoint,
          })
        }
        // An abort during teardown must not write at all — see panelAliveRef.
        // Anything else gets the same treatment as a backend error: ask
        // Chatwoot, keep what streamed, apologise only as a last resort.
        if (panelAliveRef.current) {
          updateTurn(botTurnId, { isStreaming: false })
          await finishFailedTurn(botTurnId, accumulated)
        }
      } finally {
        setIsSending(false)
        // The turn is over by every exit path (done, error, handoff, abort) —
        // never leave a progress line stranded, and let queued Chatwoot
        // messages through now that the dedup signals are settled.
        setAiStatus(null)
        streamInFlightRef.current = false
        flushPendingWsMessages()
      }
    },
    [
      isSending,
      scope,
      country,
      locale,
      pageUrl,
      pageTitle,
      entryPoint,
      session.conversation_id,
      session.source_id,
      session.turns.length,
      appendTurn,
      prependTurns,
      updateTurn,
      flushPendingWsMessages,
      noteChatwootMessage,
      noteHandoff,
      finishFailedTurn,
      setConversation,
      setEmailProvided,
      echoLauncherLine,
      handedOff,
      promptVariant,
      businessHoursOpen,
      t,
    ]
  )

  // ── Auto-submit a prompt supplied by the launcher teaser ──
  // Gated on `hydrated` so the submit runs *after* the hydration
  // effect has committed — otherwise React Strict Mode's double-
  // invocation of the hydration effect overwrites the appended turns
  // with a stale `readStorage()` snapshot.
  const autoSubmitted = useRef(false)
  useEffect(() => {
    if (autoSubmitted.current) return
    if (!initialPrompt) return
    if (!hydrated) return
    autoSubmitted.current = true
    void submit(initialPrompt, initialPromptKey)
  }, [initialPrompt, initialPromptKey, submit, hydrated])

  // ── Starter prompt select ──
  const handleStarterPromptSelect = useCallback(
    (prompt: string, index: number) => {
      track.chat.starterPromptSelected({
        scope,
        prompt_key: `${promptVariantId}.${index}`,
        prompt_index: index,
        prompt_variant: promptVariant,
      })
      // Same key string as the PostHog event, so the trace and the funnel
      // cross-reference 1:1.
      void submit(prompt, `${promptVariantId}.${index}`)
    },
    [scope, promptVariantId, promptVariant, submit]
  )

  // ── Follow-up chip select ──
  const handleFollowupSelect = useCallback(
    (
      chip: { text: string; key: string },
      index: number,
      source: "generated" | "static"
    ) => {
      track.chat.followupClicked({
        scope,
        conversation_id: session.conversation_id ?? undefined,
        turn_index: session.turns.length,
        rank: index,
        prompt_key: chip.key,
        source,
      })
      // The chip's own key carries provenance to glue, same as a starter prompt.
      void submit(chip.text, chip.key)
    },
    [scope, session.conversation_id, session.turns.length, submit]
  )

  // ── Abort in-flight stream on unmount ──
  useEffect(() => {
    // Set on the way in, not just at declaration: a ref survives React Strict
    // Mode's mount → cleanup → mount cycle, so a flag only ever lowered in
    // cleanup would latch false on the second mount and disable every failure
    // path for the rest of the panel's life. Same reason `caughtUp` is reset
    // here: this effect runs once per mount, which is exactly the scope the
    // catch-up below wants.
    panelAliveRef.current = true
    caughtUp.current = false
    return () => {
      // Order matters: the flag has to be down before the abort, because the
      // abort is what wakes `submit`'s catch.
      panelAliveRef.current = false
      streamAbortRef.current?.abort()
    }
  }, [])

  // ── Catch up on what happened while the panel was gone ──
  // The Chatwoot subscription lives in this component, so closing the panel
  // takes it down with it: a bot answer that landed after the stream was cut,
  // or an agent reply sent while the panel was shut, reaches Chatwoot but never
  // this transcript. On reopen, ask for anything past the high-water mark.
  const catchUpOnMissedMessages = useCallback(async () => {
    const conversationId = session.conversation_id
    const sourceId = session.source_id
    // The high-water mark is an optimisation, not a prerequisite: it narrows
    // what Chatwoot has to return. Correctness comes from `isBotEcho`'s content
    // match below, which is what keeps an already-rendered answer from being
    // appended twice. A first turn that was cut never got a `chatwoot_echo`, so
    // it has no mark at all — and that's exactly the case worth healing.
    const afterId = session.last_chatwoot_message_id
    if (!conversationId || !sourceId) return

    let messages
    try {
      messages = await fetchConversationMessages({
        conversationId,
        sourceId,
        afterId,
      })
    } catch (err) {
      captureChatError(err, {
        scope,
        country,
        locale,
        conversation_id: conversationId,
        prompt_variant: promptVariant,
        entry_point: entryPoint,
      })
      return
    }
    if (!panelAliveRef.current) return

    const missed = selectOutgoingMessages(messages, { afterId }).filter(
      (m) =>
        !isBotEcho(
          { messageId: m.id, content: m.content },
          {
            suppressedIds: suppressedChatwootIds.current,
            suppressedTexts: suppressedChatwootTexts.current,
            turns: turnsRef.current,
          }
        )
    )

    // Bot and human messages are indistinguishable by sender (glue posts as a
    // Chatwoot *user*), so lean on the transcript: before any human has replied
    // an outgoing message is Ella's; after that it's the team's. Worst case a
    // first human reply that arrived while the panel was closed shows as a bot
    // turn — mild, and never the reverse (Ella is never given an agent's name).
    const handedToHuman = turnsRef.current.some((turn) => turn.role === "human")

    // An empty bot bubble is the headstone of a turn whose stream died with the
    // panel. Nothing can ever fill it — the fetch above is what recovers that
    // answer — so take it back rather than leaving an empty bubble on screen.
    dropLastTurn((turn) => turn.role === "bot" && turn.content === "")

    missed.forEach((m) => {
      // Accounted for: the polling bridge's first tick after a socket flap
      // fetches unbounded (its cursor is per-mount) and would redeliver this
      // very message — the Set is what drops that copy.
      processedWsMessageIds.current.add(m.id)
      appendTurn({
        role: handedToHuman ? "human" : "bot",
        content: m.content,
        ts: m.createdAt ?? nowIso(),
        agentName: handedToHuman ? m.senderName : undefined,
        agentAvatarUrl: handedToHuman ? m.senderAvatarUrl : undefined,
      })
      noteChatwootMessage(m.id)
    })

    // Something outgoing arrived while the panel was shut, and the conversation
    // was waiting on a person — after a handoff glue stops answering, so that
    // message came from the team. Settle the wait: the persisted handoff instant
    // is older than the threshold by now, and without this the slow-reply email
    // ask would appear directly underneath the agent's reply. The Chatwoot
    // subscription can't cover this — it lives in this panel, so it wasn't
    // listening while the panel was gone.
    if (missed.length > 0 && session.handoff_at !== null) {
      setHumanReplied(true)
      clearHandoff()
    }
  }, [
    session.conversation_id,
    session.source_id,
    session.last_chatwoot_message_id,
    session.handoff_at,
    appendTurn,
    dropLastTurn,
    noteChatwootMessage,
    clearHandoff,
    scope,
    country,
    locale,
    promptVariant,
    entryPoint,
  ])

  useEffect(() => {
    // Once per mount, and the guard is load-bearing: `catchUpOnMissedMessages`
    // is rebuilt whenever the session ids change, and they change *during* a
    // turn (the `session` SSE event). Without the guard this effect re-fired
    // mid-answer and cleared `isStreaming` on the live turn.
    if (!hydrated || caughtUp.current) return
    caughtUp.current = true
    // A restored transcript can carry `isStreaming: true` on its last turn — the
    // panel was unmounted mid-answer. Nothing is streaming now, so stop the
    // indicator spinning forever.
    const last = turnsRef.current[turnsRef.current.length - 1]
    if (last?.isStreaming) updateTurn(last.id, { isStreaming: false })
    void catchUpOnMissedMessages()
  }, [hydrated, updateTurn, catchUpOnMissedMessages])

  // ── Out-of-hours fallback ──
  // When a handoff fires (or is requested) outside business hours we
  // surface an inline email/phone capture form instead of the normal
  // "connecting you to the team" message. The flag lives at the panel
  // level so follow-up system turns stay consistent.

  const handleOutOfHoursSubmitted = useCallback(
    (values: { email: string; phone: string; message: string }) => {
      setShowOutOfHoursForm(false)
      const confirmed = t("ui.outOfHours.submitted", { email: values.email })
      appendTurn({
        role: "system",
        content: confirmed,
        ts: nowIso(),
      })
      echoLauncherLine("out_of_hours_submitted", confirmed)
    },
    [appendTurn, echoLauncherLine, t]
  )

  // ── Close handler ──
  const handleClose = useCallback(() => {
    setPanelOpen(false)
    track.chat.closed({
      scope,
      conversation_id: session.conversation_id ?? undefined,
      turn_count: session.turns.length,
      handed_off: handedOff,
    })
    onClose()
  }, [
    scope,
    session.conversation_id,
    session.turns.length,
    handedOff,
    setPanelOpen,
    onClose,
  ])

  // ── Form submit ──
  const canSend = !!inputValue.trim() && !isSending && !rateLimited
  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!canSend) return
      const val = inputValue
      setInputAndPersistDraft("")
      // Drop the "customer is typing" indicator the instant the message is sent.
      typingEmit.stop()
      void submit(val)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setInputAndPersistDraft is a stable useCallback defined below
    [canSend, inputValue, submit, typingEmit]
  )

  // Enter sends, shift+enter breaks the line — the convention every chat app
  // shares. `isComposing` guards IME candidate selection, where Enter commits
  // the candidate and must not also fire the message.
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return
      e.preventDefault()
      handleFormSubmit(e)
    },
    [handleFormSubmit]
  )

  // Grow the composer with the message. The ceiling is the `max-h-*` class on
  // the textarea, not a number here — CSS clamps the height we set, so a pasted
  // biomarker list scrolls inside the composer instead of eating the
  // transcript, and there's one source of truth for the limit.
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = "auto"
    // The textarea is `box-sizing: border-box`, so its `height` includes the
    // border but `scrollHeight` does not — setting `height = scrollHeight`
    // leaves the content ~2px (top+bottom border) too tall for its box, and the
    // browser shows a permanent scrollbar even at a single row. Add the border
    // back so the field fits its content exactly.
    const verticalBorder = el.offsetHeight - el.clientHeight
    el.style.height = `${el.scrollHeight + verticalBorder}px`
    // Only surface the scrollbar once the content hits the `max-h-*` ceiling;
    // below it the field grows to fit, so a scrollbar would be pure chrome noise.
    el.style.overflowY = el.scrollHeight > el.clientHeight ? "auto" : "hidden"
  }, [inputValue])

  // Lift the lockout on its own rather than waiting for the next unrelated
  // render (see the `rateLimited` declaration).
  useEffect(() => {
    if (!rateLimited) return
    const id = window.setTimeout(
      () => setRateLimited(false),
      RATE_LIMIT_LOCKOUT_MS
    )
    return () => window.clearTimeout(id)
  }, [rateLimited])

  const hasUserTurn = session.turns.some((t) => t.role === "user")

  // Follow-up chips belong to the LATEST finished bot turn only — showing them
  // under every historical answer would clutter the transcript, and once the
  // visitor sends the next message this turn is no longer last so they clear.
  const lastTurn = session.turns[session.turns.length - 1]
  const rawFollowupChips =
    lastTurn && lastTurn.role === "bot" && !lastTurn.isStreaming
      ? (lastTurn.followups ?? null)
      : null
  // Drop any chip the visitor has already asked. A tapped chip is sent as the
  // next user message, so it lives in the transcript — and a chip the visitor
  // typed by hand is just as answered. Re-offering either wastes a slot on a
  // question already covered. Match on normalized text (trim + lowercase).
  const askedUserTexts = new Set(
    session.turns
      .filter((turn) => turn.role === "user")
      .map((turn) => turn.content.trim().toLowerCase())
  )
  const followupChips =
    rawFollowupChips?.filter(
      (chip) => !askedUserTexts.has(chip.text.trim().toLowerCase())
    ) ?? null
  const followupSource = lastTurn?.followupSource ?? "generated"

  // Transient progress line for the streaming bot turn. Falls back to the
  // default "Thinking…" when no progress event has arrived.
  const statusLabel = ((): string => {
    if (!aiStatus) return t("ui.thinking")
    if (
      aiStatus.phase === "searching" &&
      aiStatus.index != null &&
      aiStatus.total != null
    ) {
      return t("status.searching", {
        index: String(aiStatus.index),
        total: String(aiStatus.total),
      })
    }
    // A multi-topic scan without known bounds reads the same as a single lookup.
    if (aiStatus.phase === "searching") return t("status.retrieving")
    if (aiStatus.phase === "understanding") return t("status.understanding")
    return t("status.retrieving")
  })()

  // ── Inline email-collect ──
  // Ask for an email after the first bot reply lands, when the inbox opts in
  // (the `email_collect` flag from the session event). Non-blocking: skipped
  // once provided or dismissed, and never stacked on a handoff/OOH flow.
  const hasCompletedBotTurn = session.turns.some(
    (t) => t.role === "bot" && !t.isStreaming
  )
  const showEmailCollect =
    session.email_collect_enabled &&
    !session.email_provided &&
    !emailPromptDismissed &&
    !!session.source_id &&
    session.conversation_id !== null &&
    hasCompletedBotTurn &&
    !isSending &&
    !handedOff &&
    !showOutOfHoursForm

  // ── Slow-reply email ask ──
  // Deliberately NOT folded into showEmailCollect: that guard requires the
  // email_collect flag (dark in prod) and explicitly excludes handedOff, which
  // is the only state this can ever fire in.
  //
  // Everything it keys off is read from the session rather than panel state, so
  // a visitor who collapses the panel (or navigates) while waiting still gets
  // asked — that collapsed-and-waiting visitor is the whole point. `handedOff`
  // is the local flag OR a stored instant, because the flag itself dies with the
  // panel and a stored instant only exists after a handoff.
  const slowReplyDue = useSlowReplyPrompt({
    handedOff: handedOff || session.handoff_at !== null,
    humanReplied,
    emailProvided: session.email_provided,
    dismissed: session.slow_reply_dismissed,
    thresholdMs: session.slow_reply_prompt_ms,
    handoffAt: session.handoff_at,
  })
  // The same token/conversation guard showEmailCollect carries: SlowReplyForm
  // cannot identify without the widget JWT, and submitting without one reports
  // "please enter a valid email" at someone who typed a valid one.
  const showSlowReply =
    slowReplyDue && !!session.source_id && session.conversation_id !== null

  // ── Tell Chatwoot we asked ──
  // Each ask below is a form we render client-side, so nothing reached the
  // backend until the visitor submitted — the agent timeline showed the answer
  // (or, on a decline, nothing at all) with no record of the question. One call
  // per ask, mirroring the render conditions exactly, deduped through the
  // persisted session. Fire-and-forget: see use-announce-email-ask.ts.
  useAnnounceEmailAsk({
    kind: "out_of_hours",
    shown: showOutOfHoursForm,
    authToken: session.source_id,
    conversationId: session.conversation_id,
    announced: session.email_asks_announced,
    onAnnounced: noteEmailAskAnnounced,
  })
  useAnnounceEmailAsk({
    kind: "email_collect",
    shown: showEmailCollect,
    authToken: session.source_id,
    conversationId: session.conversation_id,
    announced: session.email_asks_announced,
    onAnnounced: noteEmailAskAnnounced,
  })
  useAnnounceEmailAsk({
    kind: "slow_reply",
    shown: showSlowReply && !showOutOfHoursForm,
    authToken: session.source_id,
    conversationId: session.conversation_id,
    // The wait is the reason this ask exists, so the note carries it.
    handoffAt: session.handoff_at,
    announced: session.email_asks_announced,
    onAnnounced: noteEmailAskAnnounced,
  })

  // ── Draft input persistence ──
  // Restore once on mount; persist synchronously on each change so the
  // draft survives even if the page reloads before React effects run.
  const draftRestored = useRef(false)
  useEffect(() => {
    if (draftRestored.current) return
    draftRestored.current = true
    if (typeof window === "undefined") return
    try {
      const saved = sessionStorage.getItem("ahead-chat-draft")
      if (saved) setInputValue(saved)
    } catch {
      // sessionStorage blocked — accept ephemeral drafts
    }
  }, [])
  const setInputAndPersistDraft = useCallback((val: string) => {
    setInputValue(val)
    try {
      if (val) sessionStorage.setItem("ahead-chat-draft", val)
      else sessionStorage.removeItem("ahead-chat-draft")
    } catch {
      // ignore quota / blocked storage
    }
  }, [])

  // ── Tab attention: flash title + favicon when tab is hidden ──
  // Only counts new turns (post-mount); uses a ref to avoid bumping on
  // restored sessionStorage state on initial render.
  const [unreadInBackground, setUnreadInBackground] = useState(0)
  const initialTurnCount = useRef<number | null>(null)
  useEffect(() => {
    if (typeof document === "undefined") return
    if (initialTurnCount.current === null) {
      initialTurnCount.current = session.turns.length
      return
    }
    const last = session.turns[session.turns.length - 1]
    if (!last) return
    if (last.role !== "bot" && last.role !== "human") return
    if (document.visibilityState === "hidden") {
      setUnreadInBackground((c) => c + 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when turns count changes, not on content mutations
  }, [session.turns.length])
  useEffect(() => {
    if (typeof document === "undefined") return
    function onVis(): void {
      if (!document.hidden) setUnreadInBackground(0)
    }
    document.addEventListener("visibilitychange", onVis)
    return () => document.removeEventListener("visibilitychange", onVis)
  }, [])
  useTabAttention({
    unreadCount: unreadInBackground,
    alertTitle: t("ui.tab_alert_title", { count: String(unreadInBackground) }),
  })

  /**
   * Move to the same page in the offered language. The locale lives in the URL
   * (`/{country}/{locale}/...`), so this is a navigation, not client state —
   * which also means the next chat turn is sent with the new locale and the
   * knowledge base is retrieved in it. A full navigation (rather than a soft
   * push) is deliberate: the conversation is keyed to the old locale and
   * carrying it across languages would produce a half-translated transcript.
   */
  const handleLocaleSwitch = useCallback((target: string) => {
    const next = switchLocaleInPath(
      window.location.pathname + window.location.search + window.location.hash,
      target
    )
    if (!next) return
    window.location.assign(next)
  }, [])

  // ── WhatsApp handoff URL ──
  const whatsappHref = (() => {
    const phone = businessHoursSettings.phone.replace(/[^\d]/g, "")
    if (!phone) return null
    const text = encodeURIComponent(t("ui.whatsapp_prefill"))
    return `https://wa.me/${phone}?text=${text}`
  })()

  // Raw strings because formatRelativeTime does the `{n}` substitution
  // itself (next-intl would complain about missing `n` otherwise).
  const timeLabels: RelativeTimeLabels = {
    justNow: t("ui.time_just_now"),
    minutesAgo: (t.raw as (k: string) => string)("ui.time_minutes_ago"),
    hoursAgo: (t.raw as (k: string) => string)("ui.time_hours_ago"),
  }

  return (
    <div
      className={cn(
        "flex h-full flex-col overflow-hidden bg-white",
        !hideHeader && "rounded-[16px] shadow-xl"
      )}
    >
      {/* Header — team bubble stack + status */}
      {!hideHeader && (
        <div className="flex items-start gap-3 border-b border-neutral-100 bg-gradient-to-b from-neutral-50/90 to-white px-4 pt-4 pb-3">
          <div className="flex flex-1 items-center gap-3">
            <div className="flex -space-x-2">
              {LAUNCHER_TEAM.map((member) => (
                <span
                  key={member.src}
                  className="h-9 w-9 overflow-hidden rounded-full border-2 border-white ring-1 ring-black/[0.04]"
                >
                  <Image
                    src={member.src}
                    alt={member.alt}
                    width={36}
                    height={36}
                    className="h-full w-full object-cover"
                  />
                </span>
              ))}
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="text-deep-blue-950 truncate text-sm font-semibold">
                {t("ui.header_title")}
              </span>
              <span
                className="flex items-center gap-1.5 text-xs text-neutral-500"
                // Names the state the four branches below resolve to, so tests
                // can assert the priority logic without pinning English copy
                // (which every translation would otherwise red).
                data-chat-status={
                  agentsOnline === true
                    ? "online"
                    : agentsOnline === null
                      ? "checking"
                      : businessHoursOpen !== false
                        ? "available-shortly"
                        : "offline"
                }
              >
                {/* ds-typo-ignore — intentional small status line.
                  Three states, in priority order:
                  1. agents live in Chatwoot (green)
                  2. no live agents but still in business hours (amber,
                     "checking availability") — handoff still works so
                     we don't push users into the out-of-hours form.
                  3. out of business hours (grey) — the AI stays on;
                     the team will follow up next business day. */}
                {agentsOnline === true ? (
                  // A human teammate is live in Chatwoot.
                  <>
                    <span className="bg-deep-green-500 h-1.5 w-1.5 shrink-0 rounded-full" />
                    {t("ui.header_subtitle_online")}
                  </>
                ) : agentsOnline === null ? (
                  // Transient: the first availability poll is still in flight.
                  <>
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                    {t("ui.header_subtitle_checking")}
                  </>
                ) : businessHoursOpen !== false ? (
                  // In business hours, no human live this moment — they'll pick up.
                  <>
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                    {t("ui.header_subtitle_available_shortly")}
                  </>
                ) : (
                  // Out of business hours — the AI stays on, the team follows up.
                  <>
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400" />
                    {t("ui.header_subtitle_offline")}
                  </>
                )}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
            aria-label={t("ui.launcher_close")}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 5L5 15M5 5l10 10" />
            </svg>
          </button>
        </div>
      )}

      {/* Message area — MessageScroller owns anchoring + jump-to-latest.
          `end` (the default): ordinary replies follow their tokens to the
          bottom, unchanged. A marker turn is the exception — its tall,
          all-at-once MarkerBreakdown card would otherwise auto-scroll past the
          prose to the very bottom of the card. PinMarkerTurnTop anchors that
          reply's TOP into view instead, so the prose reads from the start and
          the card fills below (AHD marker-hybrid Q2). Scoped to marker turns so
          streaming text replies keep following their tokens. */}
      <MessageScrollerProvider autoScroll defaultScrollPosition="end">
        <PinMarkerTurnTop
          turns={session.turns}
          viewportRef={scrollViewportRef}
        />
        <MessageScroller className="min-h-0 flex-1">
          <MessageScrollerViewport
            ref={scrollViewportRef}
            className="px-4 py-3"
          >
            <MessageScrollerContent className="gap-3">
              {session.turns.map((turn, i) => {
                // Attribution lives on the LAST turn of each group of
                // consecutive same-sender messages (change of role or,
                // for humans, change of agent name).
                const next = session.turns[i + 1]
                const isLastInGroup =
                  !next ||
                  next.role !== turn.role ||
                  (turn.role === "human" && next.agentName !== turn.agentName)
                return (
                  <MessageScrollerItem
                    key={`${i}-${turn.ts}`}
                    messageId={String(i)}
                    scrollAnchor={i === session.turns.length - 1}
                  >
                    <ChatMessage
                      role={turn.role}
                      content={turn.content}
                      isStreaming={turn.isStreaming}
                      variant="bubbled"
                      agentName={turn.agentName}
                      agentAvatarUrl={turn.agentAvatarUrl}
                      timestamp={turn.ts}
                      isCrisis={turn.isCrisis}
                      showAttribution={isLastInGroup}
                      timeLabels={timeLabels}
                      botAgentName={t("ui.bot_agent_name")}
                      botRoleLabel={t("ui.bot_role_label")}
                      humanRoleLabel={t("ui.human_role_label")}
                      thinkingLabel={
                        turn.isStreaming ? statusLabel : t("ui.thinking")
                      }
                      sources={turn.sources}
                      sourcesLabel={t("ui.sources")}
                      medicalNotice={turn.medicalNotice}
                      medicalNoticeLabel={t("ui.medical_disclaimer")}
                      attachments={turn.attachments}
                      markerBreakdown={turn.markerBreakdown}
                      pricingCard={turn.pricingCard}
                      localeSwitch={
                        turn.localeSwitch
                          ? {
                              actionLabel: t("ui.switch_locale_action", {
                                locale: languageDisplayName(
                                  turn.localeSwitch,
                                  locale
                                ),
                              }),
                              onSwitch: () =>
                                handleLocaleSwitch(turn.localeSwitch!),
                            }
                          : undefined
                      }
                    />
                  </MessageScrollerItem>
                )
              })}

              {/* Transient welcome typing indicator — swapped for the
                  welcome turn once the timeout fires. */}
              {showWelcomeIndicator && (
                <div className="flex items-start">
                  <div className="text-foreground bg-muted max-w-[85%] rounded-2xl px-4 py-2">
                    <TypingIndicator mode="thinking" label={t("ui.thinking")} />
                  </div>
                </div>
              )}

              {/* Human typing placeholder (dots), shown after handoff
                  while waiting for the first human reply. */}
              {humanTyping && (
                <div className="flex items-start">
                  <div className="text-foreground bg-muted max-w-[85%] rounded-2xl px-4 py-2">
                    <TypingIndicator mode="dots" label={t("ui.human_typing")} />
                  </div>
                </div>
              )}

              {/* Starter prompts — shown inline after the welcome so the
                  user can tap one instead of typing. Hidden as soon as
                  there's a user turn in the transcript. Gated on
                  `hydrated` so reopening a panel with a prior conversation
                  doesn't flash the prompts before the transcript loads. */}
              {hydrated &&
                !hasUserTurn &&
                starterPrompts.length > 0 &&
                !showWelcomeIndicator && (
                  <StarterPrompts
                    prompts={starterPrompts}
                    onSelect={handleStarterPromptSelect}
                  />
                )}

              {/* Follow-up chips beneath the latest bot answer — reuses the
                  starter-prompts component in its compact `followup` variant:
                  a small heading, ↳ affordance, and gentle enter animation
                  (renders text, never innerHTML). */}
              {followupChips && followupChips.length > 0 && (
                <StarterPrompts
                  variant="followup"
                  heading={t("ui.followups_heading")}
                  prompts={followupChips.map((f) => f.text)}
                  onSelect={(_prompt, index) =>
                    handleFollowupSelect(
                      followupChips[index],
                      index,
                      followupSource
                    )
                  }
                />
              )}

              {showOutOfHoursForm && (
                <OutOfHoursForm
                  conversationId={session.conversation_id}
                  authToken={session.source_id}
                  country={country}
                  onSubmitted={handleOutOfHoursSubmitted}
                />
              )}
              {showEmailCollect && (
                <EmailCollectForm
                  authToken={session.source_id}
                  conversationId={session.conversation_id}
                  onSubmitted={setEmailProvided}
                  onDismiss={() => setEmailPromptDismissed(true)}
                />
              )}
              {showSlowReply && !showOutOfHoursForm && (
                <SlowReplyForm
                  authToken={session.source_id}
                  conversationId={session.conversation_id}
                  waitedMs={
                    session.handoff_at ? Date.now() - session.handoff_at : 0
                  }
                  onSubmitted={(email) => {
                    setEmailProvided()
                    const confirmed = t("ui.slowReply.submitted", { email })
                    appendTurn({
                      role: "system",
                      content: confirmed,
                      ts: nowIso(),
                    })
                    echoLauncherLine("slow_reply_submitted", confirmed)
                  }}
                  onDismiss={dismissSlowReply}
                />
              )}
              {/* Read-receipt sentinel: sits below the last message so an
                  IntersectionObserver can tell when the newest messages are
                  actually on screen (see useReadReceipts). Zero-height, no
                  visual footprint. */}
              <div ref={seenAnchorRef} aria-hidden="true" className="h-px" />
            </MessageScrollerContent>
          </MessageScrollerViewport>
          <MessageScrollerButton />
        </MessageScroller>
      </MessageScrollerProvider>

      {/* Footer */}
      <div className="border-t border-neutral-100 px-4 py-3">
        {handedOff && whatsappHref && (
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-deep-blue-800 hover:text-deep-blue-950 mb-2 inline-flex items-center gap-1.5 text-xs font-medium transition-colors"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.46 0 .1 5.36.1 11.96a11.85 11.85 0 0 0 1.6 5.97L0 24l6.22-1.63a11.94 11.94 0 0 0 5.83 1.49h.01c6.6 0 11.96-5.36 11.96-11.96 0-3.2-1.24-6.2-3.5-8.42ZM12.06 21.84h-.01a9.92 9.92 0 0 1-5.06-1.39l-.36-.21-3.69.97.99-3.6-.24-.37a9.92 9.92 0 0 1-1.52-5.28c0-5.49 4.47-9.96 9.96-9.96 2.66 0 5.16 1.04 7.04 2.92a9.93 9.93 0 0 1 2.92 7.05c0 5.49-4.47 9.87-9.99 9.87Z" />
            </svg>
            {t("ui.whatsapp_link")}
          </a>
        )}
        {inputValue.length >= COMPOSER_COUNTER_THRESHOLD && (
          // Digits only — no copy to translate, and `maxLength` is what
          // actually enforces the cap; this is just the warning that it's close.
          <div
            aria-hidden="true"
            className="type-label-sm mb-1 text-right text-neutral-400"
          >
            {inputValue.length} / {COMPOSER_MAX_LENGTH}
          </div>
        )}
        <form onSubmit={handleFormSubmit} className="flex items-center gap-2">
          <div className="flex-1">
            {/* Deliberately not disabled while a turn is streaming: an answer
                takes a few seconds and people compose their follow-up during
                it. Only the backend's rate-limit lockout takes the composer
                away; `canSend` is what holds the send itself until the
                in-flight turn lands. */}
            <textarea
              ref={inputRef}
              rows={1}
              value={inputValue}
              onChange={(e) => {
                setInputAndPersistDraft(
                  e.target.value.slice(0, COMPOSER_MAX_LENGTH)
                )
                typingEmit.onInput()
              }}
              onKeyDown={handleInputKeyDown}
              placeholder={t("ui.placeholder")}
              maxLength={COMPOSER_MAX_LENGTH}
              disabled={rateLimited}
              className={cn(
                // `max-h-[140px]` (~six lines) is the composer's ceiling — the
                // auto-grow effect sets an unclamped height and lets CSS cap it.
                // `text-base` (16px) on mobile stops iOS Safari auto-zooming the
                // page when the field is focused; `sm:text-sm` keeps 14px on
                // desktop where there is no zoom behaviour.
                "text-foreground max-h-[140px] w-full resize-none overflow-y-hidden rounded-[12px] border border-neutral-200 px-3 py-2 text-base transition-colors outline-none placeholder:text-neutral-400 sm:text-sm",
                "focus:border-deep-blue-800 focus:ring-deep-blue-50 focus:ring-2",
                "disabled:opacity-60"
              )}
            />
          </div>
          <button
            type="submit"
            disabled={!canSend}
            className={cn(
              "bg-deep-blue-950 hover:bg-deep-blue-900 shrink-0 rounded-full px-4 py-2 text-sm font-medium text-white transition-colors",
              "disabled:opacity-50"
            )}
          >
            {t("ui.send")}
          </button>
        </form>
      </div>
    </div>
  )
}
